/**
 * Unified Table component — the single table standard for this project.
 *
 * Architecture:
 *  - Config-driven toolbar: `filters` (left) and `actions` (right) accept typed
 *    config objects instead of raw ReactNodes, ensuring a consistent toolbar
 *    layout on every page without any per-page custom markup.
 *  - Grouped optional features: `pagination`, `selection`, `expand`, and `sort`
 *    are each passed as a single config object so enabling a feature is atomic.
 *  - Internal sub-components handle skeleton loading, empty, and error states so
 *    call sites never need to branch on those conditions themselves.
 *  - Fully generic over the row type T; no `any` escapes.
 */

import {
  type ChangeEvent,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  InboxIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { MOBILE_QUERY } from "@/components/layout/layout.constants";

import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button/buttonVariants";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "./Pagination";
import { useTranslation } from "react-i18next";
import { RowCards } from "./RowCards";

// ─── Column Definition ────────────────────────────────────────────────────────

export interface ColumnDef<T> {
  /** Field key on the row object, or a virtual key (e.g. "actions") when render is provided. */
  key: string;
  label: React.ReactNode;
  /**
   * Custom cell renderer. When omitted the raw value at `row[key]` is rendered
   * as a ReactNode (useful when the row object already carries pre-built JSX).
   */
  render?: (row: T, index: number) => React.ReactNode;
  width?: string | number;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  sticky?: boolean;
  className?: string;
}

// ─── Filter Config ────────────────────────────────────────────────────────────

export type TableFilterDef =
  | {
      type: "select";
      key: string;
      label: string;
      placeholder?: string;
      options: { label: string; value: string }[];
      value: string;
      onChange: (value: string) => void;
      hidden?: boolean;
    }
  | {
      type: "custom";
      key: string;
      /** Render arbitrary filter UI (e.g. a FilterButton that opens a modal). */
      render: () => React.ReactNode;
      hidden?: boolean;
    };

// ─── Action Config ────────────────────────────────────────────────────────────

export type TableActionDef =
  | {
      type?: "button";
      key: string;
      label: React.ReactNode;
      icon?: React.ReactNode;
      variant?: "primary" | "outline-solid" | "ghost" | "danger";
      onClick: () => void;
      disabled?: boolean;
      hidden?: boolean;
    }
  | {
      type: "link";
      key: string;
      label: React.ReactNode;
      icon?: React.ReactNode;
      variant?: "primary" | "outline-solid" | "ghost" | "danger";
      href: string;
      hidden?: boolean;
    }
  | {
      type: "custom";
      key: string;
      render: () => React.ReactNode;
      hidden?: boolean;
    };

// ─── Feature Configs ──────────────────────────────────────────────────────────

/**
 * Module-level so the fallback is one array, not a new one per render — the
 * memo below has it as a dependency.
 */
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface PaginationDef {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  /** Shown as "X items" next to pagination when provided. */
  totalItems?: number;
  /**
   * Rows per page, and the sizes offered. Pass `onPageSizeChange` to render
   * the control; without it the table pages at whatever size the caller asked
   * the API for and the user has no say, which is the state every table in
   * payroll settings was in.
   */
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
}

export interface SelectionDef {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Row field used as the unique ID. Defaults to "id". */
  rowIdKey?: string;
  /** IDs that cannot be selected. Checkbox rendered disabled. */
  disabledIds?: string[];
}

export interface ExpandDef<T> {
  /** Renders the expanded content below a row when toggled. */
  render: (row: T) => React.ReactNode;
}

export interface SortDef {
  /** Currently sorted column key. */
  key: string;
  direction: "asc" | "desc";
  onSort: (key: string, direction: "asc" | "desc") => void;
}

// ─── Main Props ───────────────────────────────────────────────────────────────

export interface TableProps<T extends object> {
  columns: ColumnDef<T>[];
  data: T[];

  // ── Non-happy-path states ──────────────────────────────────────────────────
  loading?: boolean;
  /** When set, renders an error state instead of the table body. */
  error?: string;
  /** Heading shown in the error state. Defaults to "Something went wrong". */
  errorTitle?: string;
  /** Called when the user clicks Retry in the error state. */
  onRetry?: () => void;
  /** Heading shown in the empty state. Defaults to "No results found". */
  emptyTitle?: string;
  /** Supporting message shown in the empty state. Defaults to a generic hint. */
  emptyMessage?: string;
  /**
   * When provided, a "Reset filters" button is shown in the empty state.
   * Pass this only when filters are actively applied so the action is contextual.
   */
  onResetFilters?: () => void;

  // ── Toolbar ────────────────────────────────────────────────────────────────
  /** Controlled search value. Providing this prop shows the search bar. */
  searchText?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Inline filter controls rendered left of the toolbar spacer.
   * Use `type: "select"` for column filter dropdowns.
   * Use `type: "custom"` for custom filter UIs such as FilterButton.
   */
  filters?: TableFilterDef[];
  /**
   * Action buttons rendered right of the toolbar spacer.
   * Use `type: "button"` for onClick actions.
   * Use `type: "link"` for React Router navigation.
   * Use `type: "custom"` for any other element.
   */
  actions?: TableActionDef[];

  // ── Optional features (all disabled by default) ────────────────────────────
  /** Grouped pagination config. Pagination bar is hidden when omitted or totalPages ≤ 1. */
  pagination?: PaginationDef;
  /** Row selection via checkboxes. */
  selection?: SelectionDef;
  /** Expandable row panel. */
  expand?: ExpandDef<T>;
  /** Controlled sort state. Sortable columns only become interactive when this is provided. */
  sort?: SortDef;

  // ── Row behaviour ──────────────────────────────────────────────────────────
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: (row: T, index: number) => string;
  /** When provided, renders a full-width sub-row beneath each data row (e.g. thin progress bar on mobile). */
  renderSubRow?: (row: T, index: number) => React.ReactNode;

  /**
   * Below `MOBILE_QUERY`, render each row as a stacked card instead of a row
   * in the grid. Built from the same `columns`, so there is nothing to
   * configure per table and nothing to keep in sync.
   *
   * Opt-in: the table is shared by every module, and turning a layout change
   * on for all of them at once is not a change anyone reviewed. Flip it on a
   * table at a time.
   *
   * Ignored when `expand` or `renderSubRow` is in play — both draw a second
   * region attached to a row, which a card has nowhere to put. Sorting has no
   * affordance in the card layout either; a sortable table that turns this on
   * loses the ability to re-sort on a phone.
   *
   * A card is clickable where `onRowClick` is set, but — like the table row —
   * it is not focusable. Keyboard users reach the record through a link in the
   * first column, so a table that opts in should have one there.
   */
  cardsOnMobile?: boolean;

  /**
   * @deprecated No longer has any effect — height behaviour is controlled by
   * `scroll`. Kept for backward compatibility; safe to remove at call sites.
   */
  fillHeight?: boolean;

  /**
   * Vertical scroll behaviour.
   * - "contain" (default) The Table fills its parent's height and scrolls its
   *   body internally. Use inside height-bounded containers — modals, split
   *   panels, detail panes, or any card that must not grow past a fixed height.
   * - "flow" The Table grows to its full content height and lets the outer
   *   page (`<main>`) scroll. Use on list pages whose ancestor chain is NOT
   *   height-capped, so short laptop screens can scroll to all rows (HM-1030).
   *   Horizontal scroll for wide tables is preserved in both modes.
   */
  scroll?: "contain" | "flow";

  /** Remove the default px-5 horizontal padding from toolbar, scroll zone, and pagination. */
  noPadding?: boolean;
}

// ─── Stable defaults ──────────────────────────────────────────────────────────
// Module-level constants prevent a new array identity on every render when the
// caller omits filters/actions/selectedIds, which would defeat memoized children.

const EMPTY_FILTERS: TableFilterDef[] = [];
const EMPTY_ACTIONS: TableActionDef[] = [];
const EMPTY_SELECTED_IDS: string[] = [];

// ─── Internal hooks ───────────────────────────────────────────────────────────

function useTableSelection<T extends object>(
  data: T[],
  selection: SelectionDef | undefined,
  getRowId: (row: T, index: number) => string,
) {
  const selectedIds = selection?.selectedIds ?? EMPTY_SELECTED_IDS;

  // O(1) membership test — avoids O(n) Array.includes inside every/some loops.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const disabledIdSet = useMemo(
    () => new Set(selection?.disabledIds ?? []),
    [selection?.disabledIds],
  );

  const allRowIds = useMemo(
    () => data.map((row, i) => getRowId(row, i)),
    [data, getRowId],
  );

  // Selectable = not disabled
  const selectableRowIds = useMemo(
    () => allRowIds.filter((id) => !disabledIdSet.has(id)),
    [allRowIds, disabledIdSet],
  );

  const isAllSelected = useMemo(
    () =>
      selectableRowIds.length > 0 &&
      selectableRowIds.every((id) => selectedIdSet.has(id)),
    [selectableRowIds, selectedIdSet],
  );

  // True only when some — but not all — rows are selected (drives indeterminate).
  const isSomeSelected = useMemo(
    () =>
      !isAllSelected && selectableRowIds.some((id) => selectedIdSet.has(id)),
    [isAllSelected, selectableRowIds, selectedIdSet],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) =>
      selection?.onSelectionChange(checked ? selectableRowIds : []),
    [selection, selectableRowIds],
  );

  const handleSelectRow = useCallback(
    (id: string) => {
      if (!selection || disabledIdSet.has(id)) return;
      selection.onSelectionChange(
        selectedIdSet.has(id)
          ? selectedIds.filter((x) => x !== id)
          : [...selectedIds, id],
      );
    },
    [selection, selectedIds, selectedIdSet, disabledIdSet],
  );

  return {
    selectedIds,
    selectedIdSet,
    disabledIdSet,
    allRowIds,
    isAllSelected,
    isSomeSelected,
    handleSelectAll,
    handleSelectRow,
  };
}

function useExpandedRows(data: unknown[]) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Collapse all rows when the dataset is replaced (e.g. page change, filter
  // applied) so stale IDs from the previous page never auto-expand new rows.
  useEffect(() => {
    setExpandedIds(new Set());
  }, [data]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return { expandedIds, toggleExpand };
}

function useTableSort(sort: SortDef | undefined) {
  return useCallback(
    (key: string) => {
      if (!sort) return;
      const nextDir =
        sort.key === key && sort.direction === "asc" ? "desc" : "asc";
      sort.onSort(key, nextDir);
    },
    [sort],
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const Table = <T extends object>({
  columns,
  data,
  loading,
  error,
  errorTitle,
  onRetry,
  emptyTitle,
  emptyMessage,
  onResetFilters,
  searchText,
  onSearchChange,
  searchPlaceholder,
  filters = EMPTY_FILTERS,
  actions = EMPTY_ACTIONS,
  pagination,
  selection,
  expand,
  sort,
  onRowClick,
  rowClassName,
  cardsOnMobile = false,
  renderSubRow,
  scroll = "contain",
  noPadding,
}: TableProps<T>) => {
  const { t } = useTranslation("common");
  // Resolved here rather than as parameter defaults: default values are
  // evaluated before the hook runs, so they cannot call `t`.
  const errorTitleText = errorTitle ?? t("common:states.error_title");
  const emptyTitleText = emptyTitle ?? t("common:states.no_results");
  const emptyMessageText =
    emptyMessage ?? t("common:states.try_adjusting_filters");
  const searchPlaceholderText =
    searchPlaceholder ?? t("common:shared_ui.search_placeholder");
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const isFlow = scroll === "flow";

  const rowIdKey = selection?.rowIdKey ?? "id";

  const getRowId = useCallback(
    (row: T, index: number) =>
      String((row as Record<string, unknown>)[rowIdKey] ?? index),
    [rowIdKey],
  );

  const {
    selectedIds,
    selectedIdSet,
    disabledIdSet,
    isAllSelected,
    isSomeSelected,
    handleSelectAll,
    handleSelectRow,
  } = useTableSelection(data, selection, getRowId);

  const { expandedIds, toggleExpand } = useExpandedRows(data);
  const handleSort = useTableSort(sort);

  const colSpan = useMemo(
    () => columns.length + (selection ? 1 : 0) + (expand ? 1 : 0),
    [columns.length, selection, expand],
  );

  const hasToolbar = useMemo(
    () =>
      !!onSearchChange ||
      filters.some((f) => !f.hidden) ||
      actions.some((a) => !a.hidden),
    [onSearchChange, filters, actions],
  );

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = isSomeSelected;
  }, [isSomeSelected]);

  /**
   * Cards replace the grid on a phone, not merely hide it: rendering both and
   * toggling with `hidden sm:block` would put every checkbox and action button
   * in the DOM twice, which doubles the tab order and hands screen readers two
   * of each row.
   *
   * `expand` and `renderSubRow` keep the table — both attach a second region
   * to a row, and a card has nowhere to hang one.
   */
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const showCards = cardsOnMobile && isMobile && !expand && !renderSubRow;

  const pageSizeOptions = pagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

  /**
   * The size control is offered once there is more than one page's worth of
   * rows AT THE SMALLEST OFFERED SIZE — not once there is more than one page.
   *
   * Keyed to the smallest option because the obvious rule ("show it when
   * paging") strands the user: pick 100, everything fits on one page, the
   * pager disappears and the control goes with it, with no way back to 10.
   */
  const showPageSize = Boolean(
    pagination?.onPageSizeChange &&
      pagination.pageSize &&
      (pagination.totalPages > 1 ||
        (pagination.totalItems ?? 0) > Math.min(...pageSizeOptions)),
  );

  const showPaginationBar = Boolean(
    pagination && (pagination.totalPages > 1 || showPageSize),
  );

  /**
   * A size the URL asked for that is not one of the offered ones still has to
   * appear in the list — a `Select` whose value matches no item renders an
   * empty trigger, so `?limit=37` would show a blank box and no way to read
   * what the table is actually doing.
   */
  const sizeChoices = useMemo(() => {
    const size = pagination?.pageSize;
    if (!size || pageSizeOptions.includes(size)) return pageSizeOptions;
    return [...pageSizeOptions, size].sort((a, b) => a - b);
  }, [pagination?.pageSize, pageSizeOptions]);

  return (
    <div className={cn("flex flex-col font-sans flex-1", !isFlow && "min-h-0")}>
      {hasToolbar && (
        <div
          className={`shrink-0 bg-white border-b border-gray-100 py-3 ${noPadding ? "" : "px-5"}`}
        >
          <Toolbar
            searchText={searchText}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholderText}
            filters={filters}
            actions={actions}
            selectedCount={selectedIds.length}
          />
        </div>
      )}

      {/*
       * Scroll zone.
       * - contain: `flex-1 min-h-0 overflow-auto` — fills the bounded parent and
       *   scrolls its body internally (sticky <thead> pins within it).
       * - flow: `flex-1 overflow-x-auto` — no vertical cap, so the zone grows to
       *   its content height and the outer <main> scrolls the whole page; wide
       *   tables still scroll horizontally.
       */}
      <div
        className={cn(
          "scrollbar-thin",
          isFlow ? "flex-1 overflow-x-auto" : "flex-1 min-h-0 overflow-auto",
          !noPadding && "px-5",
        )}
      >
        {showCards ? (
          loading ? (
            <div className="flex flex-col gap-3 py-2">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-xs" />
              ))}
            </div>
          ) : error ? (
            <div className="py-10">
              <ErrorState
                title={errorTitleText}
                message={error}
                onRetry={onRetry}
              />
            </div>
          ) : data.length === 0 ? (
            <div className="py-10">
              <EmptyState
                title={emptyTitleText}
                message={emptyMessageText}
                onResetFilters={onResetFilters}
              />
            </div>
          ) : (
            <RowCards
              data={data}
              columns={columns}
              getRowId={getRowId}
              onRowClick={onRowClick}
              rowClassName={rowClassName}
              hasSelection={!!selection}
              selectedIdSet={selectedIdSet}
              disabledIdSet={disabledIdSet}
              onSelectRow={handleSelectRow}
            />
          )
        ) : (
        <table
          className={cn(
            "w-full text-xs border-separate border-spacing-x-0 font-sans",
            renderSubRow ? "border-spacing-y-0" : "border-spacing-y-2",
          )}
        >
          {/*
           * ── Column header row ──────────────────────────────────────────────
           * `sticky top-0 z-10` makes this stick inside the scroll zone above.
           * `bg-muted` on the <thead> element itself (not just the <th> cells)
           * is critical: it fills the full thead bounding box — including any
           * sub-pixel gaps at rounded corners and border-spacing areas — so
           * scrolling tbody rows never bleed through.
           */}
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="[&>th]:font-medium [&>th]:text-primary-strong [&>th]:text-start [&>th]:px-3 [&>th]:py-2.5 [&>th]:bg-muted [&>th:first-child]:rounded-s-xs [&>th:last-child]:rounded-e-xs [&>th]:whitespace-nowrap [&>th]:border-t [&>th]:border-b [&>th]:border-border [&>th:first-child]:border-s [&>th:last-child]:border-e">
              {selection && (
                <th className="w-10 text-center!">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="cursor-pointer accent-primary"
                  />
                </th>
              )}
              {expand && <th className="w-10" />}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={cn(
                    col.sortable && sort
                      ? "cursor-pointer select-none hover:text-primary transition-colors"
                      : "",
                    col.align === "center" && "text-center!",
                    col.align === "right" && "text-end!",
                    col.sticky ? "sticky start-0 z-20" : "",
                    col.className,
                  )}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && sort && (
                      <SortIndicator
                        active={sort.key === col.key}
                        direction={
                          sort.key === col.key ? sort.direction : undefined
                        }
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {renderSubRow && (
              <tr aria-hidden="true">
                <td colSpan={colSpan} className="h-2 p-0" />
              </tr>
            )}
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <SkeletonRow
                  key={i}
                  index={i}
                  columnCount={columns.length}
                  hasSelection={!!selection}
                  hasExpand={!!expand}
                />
              ))
            ) : error ? (
              <tr>
                <td colSpan={colSpan} className="h-80 align-middle text-center">
                  <ErrorState
                    title={errorTitleText}
                    message={error}
                    onRetry={onRetry}
                  />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="h-80 align-middle text-center">
                  <EmptyState
                    title={emptyTitleText}
                    message={emptyMessageText}
                    onResetFilters={onResetFilters}
                  />
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const id = getRowId(row, rowIndex);
                const isSelected = selectedIdSet.has(id);
                const isExpanded = expandedIds.has(id);

                return (
                  <Fragment key={id}>
                    {/* Spacer between row groups when using sub-rows */}
                    {renderSubRow && rowIndex > 0 && (
                      <tr aria-hidden="true">
                        <td colSpan={colSpan} className="h-2 p-0" />
                      </tr>
                    )}
                    <tr
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        // Skip clicks bubbled from React portals (dialogs, popovers, dropdowns)
                        // whose DOM is not actually inside this row.
                        if (!e.currentTarget.contains(target)) return;
                        if (target.closest("[data-no-row-click]")) return;
                        onRowClick?.(row, rowIndex);
                      }}
                      className={cn(
                        "[&>td]:border-t [&>td]:px-3 [&>td]:py-3 [&>td]:whitespace-nowrap",
                        renderSubRow
                          ? "[&>td:first-child]:border-s [&>td:first-child]:rounded-tl-xs [&>td:last-child]:border-e [&>td:last-child]:rounded-tr-xs"
                          : "[&>td]:border-b [&>td:first-child]:border-s [&>td:first-child]:rounded-s-xs [&>td:last-child]:border-e [&>td:last-child]:rounded-e-xs",
                        "hover:bg-primary/5 transition-colors duration-150",
                        onRowClick && "cursor-pointer",
                        isSelected && "bg-primary/5",
                        rowClassName?.(row, rowIndex),
                      )}
                    >
                      {selection && (
                        <td className="w-10 text-center!" data-no-row-click>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={disabledIdSet.has(id)}
                            onChange={() => handleSelectRow(id)}
                            onClick={(e) => e.stopPropagation()}
                            className="cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                          />
                        </td>
                      )}
                      {expand && (
                        <td className="w-10 text-center!" data-no-row-click>
                          <button
                            type="button"
                            onClick={() => toggleExpand(id)}
                            className="p-1 rounded-xs hover:bg-gray-100 transition-colors"
                          >
                            <ChevronRightIcon
                              className={cn(
                                "size-4 text-gray-500 transition-transform duration-200",
                                isExpanded && "rotate-90",
                              )}
                            />
                          </button>
                        </td>
                      )}
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            col.align === "center" && "text-center!",
                            col.align === "right" && "text-end!",
                            col.sticky ? "sticky start-0 z-10 bg-white" : "",
                            col.className,
                          )}
                        >
                          {col.render
                            ? col.render(row, rowIndex)
                            : ((row as Record<string, unknown>)[
                                col.key
                              ] as React.ReactNode)}
                        </td>
                      ))}
                    </tr>

                    {renderSubRow && (
                      <tr>
                        <td
                          colSpan={colSpan}
                          className="p-0 overflow-hidden border-b border-s border-e border-border rounded-bl-xs rounded-br-xs"
                        >
                          {renderSubRow(row, rowIndex)}
                        </td>
                      </tr>
                    )}

                    {expand && isExpanded && (
                      <tr>
                        <td
                          colSpan={colSpan}
                          className="border-s border-e border-b border-border rounded-b-xs bg-gray-50 px-4 py-3"
                        >
                          {expand.render(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {pagination && showPaginationBar && (
        <div
          className={cn(
            // Wraps: the bar now carries an item count, a rows-per-page select
            // and the pager, which do not fit on one line at phone width.
            "shrink-0 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-3",
            !noPadding && "px-5",
          )}
        >
          {pagination.totalItems !== undefined && (
            <span className="text-xs text-gray-500">
              {t("ui_kit.item_count", { count: pagination.totalItems })}
            </span>
          )}
          {showPageSize && (
            /*
             * A `<span>`, not a `<label>`: the control is a Radix trigger
             * BUTTON, which a label cannot be associated with — the text would
             * be visible and the control still unnamed to a screen reader.
             * `aria-label` on the trigger is what actually names it, and the
             * visible text is marked `aria-hidden` so it is not read twice.
             */
            <span className="flex items-center gap-2 text-xs text-gray-500">
              <span aria-hidden="true">{t("common:table.rows_per_page")}</span>
              <Select
                value={String(pagination.pageSize)}
                onValueChange={(v) => pagination.onPageSizeChange?.(Number(v))}
              >
                <SelectTrigger
                  aria-label={t("common:table.rows_per_page")}
                  className="h-8 w-[4.5rem]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sizeChoices.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </span>
          )}
          {pagination.totalPages > 1 && (
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              onPageChange={pagination.onPageChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

interface ToolbarProps {
  searchText?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  filters: TableFilterDef[];
  actions: TableActionDef[];
  selectedCount: number;
}

const Toolbar = memo(
  ({
    searchText,
    onSearchChange,
    searchPlaceholder,
    filters,
    actions,
    selectedCount,
  }: ToolbarProps) => {
    const { t } = useTranslation(["common"]);
    const [inputValue, setInputValue] = useState(searchText ?? "");

    // Sync when parent resets searchText externally (e.g. clear all filters)
    useEffect(() => {
      setInputValue(searchText ?? "");
    }, [searchText]);

    const handleSearchChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        onSearchChange?.(e.target.value);
      },
      [onSearchChange],
    );

    const handleSearchClear = useCallback(() => {
      setInputValue("");
      onSearchChange?.("");
    }, [onSearchChange]);

    const visibleFilters = useMemo(
      () => filters.filter((f) => !f.hidden),
      [filters],
    );
    const visibleActions = useMemo(
      () => actions.filter((a) => !a.hidden),
      [actions],
    );

    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        {onSearchChange && (
          <div className="relative max-w-[380px] grow">
            <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400 size-4 pointer-events-none" />
            <Input
              value={inputValue}
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              className="ps-9! h-[38px] text-sm placeholder:text-gray-400 border-border bg-transparent focus-visible:border-gray-400!"
            />
            {inputValue && (
              <button
                type="button"
                onClick={handleSearchClear}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XIcon className="size-4" />
              </button>
            )}
          </div>
        )}

        {/* Inline filters (left of spacer) */}
        {visibleFilters.map((filter) => {
          if (filter.type === "custom") {
            return <Fragment key={filter.key}>{filter.render()}</Fragment>;
          }
          return <SelectFilter key={filter.key} config={filter} />;
        })}

        {/* Spacer */}
        <div className="flex-1 min-w-0" />

        {/* Selection count */}
        {selectedCount > 0 && (
          <span className="text-xs text-gray-500 font-medium shrink-0">
            {t("common:table.selected", { count: selectedCount })}
          </span>
        )}

        {/* Actions (right of spacer) */}
        {visibleActions.map((action) => {
          if (action.type === "custom") {
            return <Fragment key={action.key}>{action.render()}</Fragment>;
          }

          if (action.type === "link") {
            return (
              <Link
                key={action.key}
                to={action.href}
                className={buttonVariants({
                  variant: action.variant ?? "outline-solid",
                  className: "h-9 gap-2 font-normal text-xs! shrink-0",
                })}
              >
                {action.icon && (
                  <span className="shrink-0 bg-white rounded-sxs p-px text-primary">
                    {action.icon}
                  </span>
                )}
                {action.label}
              </Link>
            );
          }

          return (
            <Button
              key={action.key}
              variant={action.variant ?? "primary"}
              disabled={action.disabled}
              onClick={action.onClick}
              className="h-9 gap-2 font-normal text-xs! shrink-0"
            >
              {action.icon && (
                <span className="shrink-0 bg-white rounded-sxs p-px text-primary">
                  {action.icon}
                </span>
              )}
              {action.label}
            </Button>
          );
        })}
      </div>
    );
  },
);

Toolbar.displayName = "TableToolbar";

// ─── Select Filter ─────────────────────────────────────────────────────────────

interface SelectFilterProps {
  config: Extract<TableFilterDef, { type: "select" }>;
}

const SelectFilter = memo(({ config }: SelectFilterProps) => (
  <Select value={config.value} onValueChange={config.onChange}>
    <SelectTrigger className="h-[38px] text-xs min-w-[140px] border-border bg-transparent">
      <SelectValue placeholder={config.placeholder ?? config.label} />
    </SelectTrigger>
    <SelectContent>
      {config.options.map((opt) => (
        <SelectItem key={opt.value} value={opt.value} className="text-xs">
          {opt.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
));

SelectFilter.displayName = "TableSelectFilter";

// ─── Sort Indicator ───────────────────────────────────────────────────────────

const SortIndicator = memo(
  ({ active, direction }: { active: boolean; direction?: "asc" | "desc" }) => (
    <span className="inline-flex flex-col ms-0.5">
      <ChevronUpIcon
        className={cn(
          "size-3 -mb-1",
          active && direction === "asc" ? "text-primary" : "text-gray-300",
        )}
      />
      <ChevronDownIcon
        className={cn(
          "size-3",
          active && direction === "desc" ? "text-primary" : "text-gray-300",
        )}
      />
    </span>
  ),
);

SortIndicator.displayName = "TableSortIndicator";

// ─── Skeleton Row ─────────────────────────────────────────────────────────────

const SkeletonRow = memo(
  ({
    index,
    columnCount,
    hasSelection,
    hasExpand,
  }: {
    index: number;
    columnCount: number;
    hasSelection: boolean;
    hasExpand: boolean;
  }) => {
    // CSS custom property cascades into the Skeleton's ::before pseudo-element,
    // giving each row its own shimmer phase so the wave feels staggered.
    const shimmerDelay = {
      "--shimmer-delay": `${index * 0.12}s`,
    } as React.CSSProperties;

    return (
      <tr className="[&>td]:border-t [&>td]:border-b [&>td]:px-3 [&>td]:py-3 [&>td:first-child]:border-s [&>td:first-child]:rounded-s-xs [&>td:last-child]:border-e [&>td:last-child]:rounded-e-xs">
        {hasSelection && (
          <td>
            <Skeleton className="w-4 h-4 mx-auto" style={shimmerDelay} />
          </td>
        )}
        {hasExpand && (
          <td>
            <Skeleton className="w-4 h-4 mx-auto" style={shimmerDelay} />
          </td>
        )}
        {Array.from({ length: columnCount }, (_, i) => (
          <td key={i}>
            <Skeleton
              style={shimmerDelay}
              className={cn(
                "h-7 rounded",
                i % 3 === 0 && "w-[80%]",
                i % 3 === 1 && "w-[55%]",
                i % 3 === 2 && "w-[70%]",
              )}
            />
          </td>
        ))}
      </tr>
    );
  },
);

SkeletonRow.displayName = "TableSkeletonRow";

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = memo(
  ({
    title,
    message,
    onResetFilters,
  }: {
    title: string;
    message: string;
    onResetFilters?: () => void;
  }) => {
    const { t } = useTranslation(["common"]);

    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="p-4 rounded-full bg-gray-100">
          <InboxIcon className="size-8 text-gray-400 stroke-[1.25]" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <p className="text-sm font-medium text-gray-700">{title}</p>
          <p className="text-xs text-gray-400 max-w-xs">{message}</p>
        </div>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="text-xs text-primary hover:underline font-medium mt-1"
          >
            {t("common:ui_kit.reset_filters")}
          </button>
        )}
      </div>
    );
  },
);

EmptyState.displayName = "TableEmptyState";

// ─── Error State ──────────────────────────────────────────────────────────────

const ErrorState = memo(
  ({
    title,
    message,
    onRetry,
  }: {
    title: string;
    message: string;
    onRetry?: () => void;
  }) => {
    const { t } = useTranslation(["common"]);

    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="p-4 rounded-full bg-red-50">
          <AlertCircleIcon className="size-8 text-red-400 stroke-[1.25]" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <p className="text-sm font-medium text-gray-700">{title}</p>
          <p className="text-xs text-gray-400 max-w-xs">{message}</p>
        </div>
        {onRetry && (
          <Button
            variant="outline"
            onClick={onRetry}
            className="h-8 text-xs font-normal mt-1"
          >
            {t("common:buttons.try_again")}
          </Button>
        )}
      </div>
    );
  },
);

ErrorState.displayName = "TableErrorState";
